import { useRemoveProductFromComparisonMutation } from "~/features/compareProducts/api/productComparisonQuery";
import { trackAddToComparisonClick } from "~/features/compareProducts/utils/analytics";
import { Box, IconButton, Stack, styled } from "~/common/mui";
import { ProductDescriptorContext } from "~/domains/product/ProductContext";
import { Product } from "~/domains/product/Product";
import CloseIcon from "@mui/icons-material/Close";
import { TooltipWithArrow } from "~/common/components/TooltipWithArrow";
import { useEffect, useRef, useState } from "react";
import { useProductComparisonCardLogic } from "~/features/compareProducts/hooks/useProductComparisonCardLogic";

interface Props {
  productCode: string;
}

const ProductComparisonTitleCard = ({ productCode }: Props) => {
  const removeMutation = useRemoveProductFromComparisonMutation();
  const { productDescriptor } = useProductComparisonCardLogic(productCode);
  const designationRef = useRef<HTMLElement | null>(null);
  const subDesignationRef = useRef<HTMLElement | null>(null);
  const [showDesignationTooltip, setShowDesignationTooltip] = useState(false);
  const [showSubDesignationTooltip, setShowSubDesignationTooltip] = useState(false);
  const showPreferredIndicator = productDescriptor?.base?.preferred;
  const handleCloseClick = async () => {
    try {
      await removeMutation.mutateAsync(productCode);
      trackAddToComparisonClick(productDescriptor?.base?.designation ?? productCode, false, "Product Comparison");
    } catch (error) {
      console.error("Failed to remove product from comparison", error);
    }
  };

  useEffect(() => {
    if (designationRef.current) {
      setShowDesignationTooltip(designationRef.current.scrollWidth > designationRef.current.clientWidth);
    }

    const typography = subDesignationRef.current?.firstElementChild as HTMLElement | null;

    if (typography) {
      setShowSubDesignationTooltip(typography.scrollWidth > typography.clientWidth);
    }
  }, [productDescriptor]);

  return (
    <>
      <ProductDescriptorContext value={productDescriptor}>
        <ComparisonTitleCardRoot>
          <IconButton variant="contained" size="small" onClick={handleCloseClick}>
            <CloseIcon />
          </IconButton>
          <Stack gap={"4px"}>
            <Stack direction="row" alignItems="center" gap={"4px"}>
              {showPreferredIndicator && <Product.PreferredIndicator noMargin />}
              <Box sx={{ minWidth: 0 }}>
                <Product.PdpLink>
                  <TooltipWithArrow title={showDesignationTooltip ? <Product.DesignationV2 /> : ""} placement="top">
                    <Product.DesignationV2
                      ref={designationRef}
                      variant="titleMedium"
                      component="b"
                      noWrap
                      sx={{
                        display: "block",
                        maxWidth: showPreferredIndicator ? 150 : 170,
                      }}
                    />
                  </TooltipWithArrow>
                </Product.PdpLink>
              </Box>
            </Stack>

            <TooltipWithArrow title={showSubDesignationTooltip ? <Product.ShortDescriptionV2 /> : ""} placement="top">
              <Box
                ref={subDesignationRef}
                sx={{
                  width: 196,
                }}
              >
                <Product.ShortDescriptionV2
                  truncate
                  variant="bodySmall"
                  color="textSecondary"
                  sx={{
                    display: "block",
                    width: "100%",
                  }}
                />
              </Box>
            </TooltipWithArrow>
          </Stack>
        </ComparisonTitleCardRoot>
      </ProductDescriptorContext>
    </>
  );
};

export default ProductComparisonTitleCard;

const ComparisonTitleCardRoot = styled("div")(({ theme }) => ({
  backgroundColor: theme.palette.primary.contrastText,
  minWidth: 228,
  width: 228,
  height: 76,
  position: "relative",
  display: "flex",
  padding: theme.spacing(2),
  color: theme.palette.text.primary,
  border: `1px solid ${theme.palette.divider}`,
  borderRadius: theme.spacing(1.5),

  "& .MuiIconButton-root": {
    position: "absolute",
    top: 8,
    right: 8,
  },
}));
